import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Combobox, Field, formatDateTime, inputClass, useAdminApi, useConfirm, useToast } from './ui';

const FILTERS = [
  ['all', 'All users'],
  ['unconfirmed', 'Unconfirmed email'],
  ['access', 'Has region/place/organizer access'],
  ['noaccess', 'No access yet'],
  ['superadmin', 'Superadmins'],
];

function AdminUsers({ data, reload, preset }) {
  const api = useAdminApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyKey, setBusyKey] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (preset && preset.filter) setFilter(preset.filter);
  }, [preset]);

  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.users.filter((user) => {
      if (q && ![user.username, user.name, user.email].filter(Boolean).join(' ').toLowerCase().includes(q)) return false;
      const access = user._count.userRegions + user._count.userPlaces + user._count.userOrganizers;
      if (filter === 'unconfirmed') return !user.isConfirmed;
      if (filter === 'access') return access > 0;
      if (filter === 'noaccess') return access === 0 && user.role !== 'superadmin';
      if (filter === 'superadmin') return user.role === 'superadmin';
      return true;
    });
  }, [data.users, search, filter]);

  const act = async (user, key, request, successMessage) => {
    setBusyKey(`${key}-${user.id}`);
    try {
      await request();
      toast(successMessage);
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusyKey('');
    }
  };

  const resendConfirmation = (user) => act(user, 'confirm', () => api(`/reconfirm/${user.id}`, { method: 'POST' }), `Confirmation email sent to ${user.email}.`);

  const resetPassword = async (user) => {
    const ok = await confirm({
      title: `Reset password for @${user.username}?`,
      message: `A new password will be generated and emailed to ${user.email}.`,
      confirmLabel: 'Reset password',
    });
    if (ok) act(user, 'reset', () => api(`/newpassword/${user.id}`, { method: 'POST' }), `Password reset email sent to ${user.email}.`);
  };

  const deleteUser = async (user) => {
    const c = user._count;
    const ok = await confirm({
      title: `Delete @${user.username}?`,
      message: `Removes the account, its API keys and its access (${c.userRegions} regions, ${c.userPlaces} places, ${c.userOrganizers} organizers). This cannot be undone.`,
      confirmLabel: 'Delete user',
      danger: true,
      requireText: user.username,
    });
    if (ok) act(user, 'delete', () => api(`/user/${user.id}`, { method: 'DELETE' }), `Deleted @${user.username}.`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search username, name or email…" className={`${inputClass} !w-72`} />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputClass} !w-auto`}>
          {FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-gray-500">{users.length} of {data.users.length}</span>
          <Button variant="primary" onClick={() => setShowInvite((open) => !open)}>{showInvite ? 'Close invite' : '+ Invite someone'}</Button>
        </div>
      </div>

      {showInvite && <InvitePanel data={data} api={api} toast={toast} onDone={() => setShowInvite(false)} />}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">User</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Access</th>
              <th className="px-4 py-2 font-medium text-right">Edits</th>
              <th className="px-4 py-2 font-medium">Joined</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-500">No users match.</td></tr>}
            {users.map((user) => {
              const c = user._count;
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/user/${user.username}`} className="font-medium hover:underline">@{user.username}</Link>
                    <div className="text-xs text-gray-500">{user.name || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{user.email}</div>
                    {user.isConfirmed ? <Badge color="green">confirmed</Badge> : <Badge color="red">unconfirmed</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    {user.role === 'superadmin' && <Badge color="purple">superadmin</Badge>}
                    {user.userRegions.map((ur) => (
                      <div key={ur.region.id} className="text-xs"><Link className="underline" to={`/region/${ur.region.urlName}`}>{ur.region.name}</Link> · {ur.role}</div>
                    ))}
                    <div className="text-xs text-gray-500">
                      {c.userPlaces} place{c.userPlaces === 1 ? '' : 's'} · {c.userOrganizers} organizer{c.userOrganizers === 1 ? '' : 's'}{c.apiKeys ? ' · API key' : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{user.edits}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDateTime(user.createdAt)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {!user.isConfirmed && <Button busy={busyKey === `confirm-${user.id}`} onClick={() => resendConfirmation(user)}>Resend confirmation</Button>}
                    <Button className="ml-2" busy={busyKey === `reset-${user.id}`} onClick={() => resetPassword(user)}>Reset password</Button>
                    {user.role !== 'superadmin' && <Button className="ml-2" variant="danger" busy={busyKey === `delete-${user.id}`} onClick={() => deleteUser(user)}>Delete</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Invite by picking the region / place / organizer from a list (no more pasting IDs)
function InvitePanel({ data, api, toast, onDone }) {
  const [email, setEmail] = useState('');
  const [target, setTarget] = useState('place');
  const [entityId, setEntityId] = useState('');
  const [role, setRole] = useState('manager');
  const [sending, setSending] = useState(false);

  const regionName = useMemo(() => Object.fromEntries(data.regions.map((r) => [r.id, r.name])), [data.regions]);
  const options = useMemo(() => {
    if (target === 'region') return data.regions.map((r) => ({ value: r.id, label: r.name }));
    const list = target === 'place' ? data.places : data.organizers;
    return list.map((item) => ({ value: item.id, label: item.name, sub: regionName[item.regionId] }));
  }, [target, data, regionName]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const body = { email: email.trim(), role, [`${target}Id`]: entityId };
      const result = await api('/admin/invite', { method: 'POST', body });
      toast(result.message || 'Invite sent.');
      setEmail('');
      setEntityId('');
      onDone();
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Invite someone to manage…">
      <form onSubmit={send} className="grid md:grid-cols-2 gap-4 max-w-3xl">
        <Field label="Email" hint="They must sign up with this address. Invites last 5 days.">
          <input type="email" required className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </Field>
        <Field label="Invite to">
          <div className="flex gap-2">
            {['place', 'organizer', 'region'].map((value) => (
              <button
                type="button"
                key={value}
                onClick={() => { setTarget(value); setEntityId(''); }}
                className={`px-3 py-2 text-sm rounded-md border capitalize ${target === value ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                {value}
              </button>
            ))}
          </div>
        </Field>
        <Field label={`Which ${target}?`}>
          <Combobox options={options} value={entityId} onChange={setEntityId} placeholder={`Search ${target}s…`} />
        </Field>
        {target !== 'region' && (
          <Field label="Role">
            <select className={inputClass} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
              <option value="gatekeeper">Gatekeeper</option>
            </select>
          </Field>
        )}
        <div className="md:col-span-2">
          <Button type="submit" variant="primary" busy={sending} disabled={!email || !entityId}>Send invite</Button>
        </div>
      </form>
    </Card>
  );
}

export default AdminUsers;
